(() => {
  const SELECTOR = 'input[data-rotating-placeholder]';

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const splitExamples = (raw) =>
    String(raw || '')
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);

  const toInt = (val, fallback) => {
    const n = Number.parseInt(String(val || ''), 10);
    return Number.isFinite(n) ? n : fallback;
  };

  class RotatingPlaceholder {
    constructor(input) {
      this.input = input;
      this.defaultText = input.getAttribute('data-rotating-placeholder-default') || 'Caută produse…';
      this.examples = splitExamples(input.getAttribute('data-rotating-placeholder-examples'));
      this.intervalMs = toInt(input.getAttribute('data-rotating-placeholder-interval'), 3000);
      this.idleResumeMs = toInt(input.getAttribute('data-rotating-placeholder-idle'), 5000);
      this.random =
        (input.getAttribute('data-rotating-placeholder-random') || '').toLowerCase() === 'true';
      this.prefix = input.getAttribute('data-rotating-placeholder-prefix') || '';

      this._idx = 0;
      this._timer = null;
      this._resumeTimeout = null;
      this._destroyed = false;
      this._char = 0;
      this._word = '';
      this._lastWord = '';
      this._phase = 'idle'; // 'typing' | 'holding' | 'deleting'
      this._minChars = 0; // keep prefix while deleting

      // Tuned to keep one full cycle around intervalMs (word start -> next word start)
      this._typeSpeedMs = 55;
      this._deleteSpeedMs = 35;
      this._gapMs = 150;
      this._minHoldMs = 200;
      this._initialDelayMs = 700;

      // Ensure a known starting placeholder
      if (!this.input.placeholder) this.input.placeholder = this.defaultText;

      this._onFocus = this._onFocus.bind(this);
      this._onBlur = this._onBlur.bind(this);
      this._onInput = this._onInput.bind(this);
      this._onVisibility = this._onVisibility.bind(this);

      input.addEventListener('focus', this._onFocus, { passive: true });
      input.addEventListener('blur', this._onBlur, { passive: true });
      input.addEventListener('input', this._onInput, { passive: true });
      document.addEventListener('visibilitychange', this._onVisibility);

      this.start();
    }

    isPaused() {
      if (this._destroyed) return true;
      if (document.hidden) return true;
      if (document.activeElement === this.input) return true;
      if ((this.input.value || '').trim().length > 0) return true;
      if (!this.examples.length) return true;
      return false;
    }

    start() {
      if (this._timer) return;
      if (prefersReducedMotion()) return;
      if (this.examples.length === 0) return;

      // Keep default placeholder briefly, then start typing loop
      this._timer = window.setTimeout(() => {
        this._timer = null;
        this._beginWord();
      }, this._initialDelayMs);
    }

    stop() {
      if (this._timer) window.clearTimeout(this._timer);
      this._timer = null;
      if (this._resumeTimeout) window.clearTimeout(this._resumeTimeout);
      this._resumeTimeout = null;
      this._phase = 'idle';
    }

    _schedule(ms, fn) {
      if (this._destroyed) return;
      if (this._timer) window.clearTimeout(this._timer);
      this._timer = window.setTimeout(() => {
        this._timer = null;
        fn();
      }, ms);
    }

    _beginWord() {
      if (this.isPaused()) return;
      const term = this._pickNextWord();
      this._lastWord = term;
      this._word = `${this.prefix}${term}`;
      this._minChars = this.prefix.length;
      this._char = this._minChars;
      this._phase = 'typing';
      this.input.placeholder = this.prefix;
      this._step();
    }

    _pickNextWord() {
      const list = this.examples;
      if (!list.length) return '';
      if (!this.random) {
        const w = list[this._idx % list.length] || '';
        this._idx += 1;
        return w;
      }

      if (list.length === 1) return list[0];
      // Random, but avoid immediate repeats
      let w = this._lastWord;
      let guard = 0;
      while (w === this._lastWord && guard < 10) {
        w = list[Math.floor(Math.random() * list.length)];
        guard += 1;
      }
      return w;
    }

    _step() {
      if (this._destroyed) return;
      if (this.isPaused()) return;

      if (this._phase === 'typing') {
        this._char += 1;
        this.input.placeholder = this._word.slice(0, this._char);

        if (this._char >= this._word.length) {
          // compute hold so full cycle is ~intervalMs
          const typeDuration = this._word.length * this._typeSpeedMs;
          const deleteDuration = this._word.length * this._deleteSpeedMs;
          const hold = Math.max(this._minHoldMs, this.intervalMs - typeDuration - deleteDuration - this._gapMs);
          this._phase = 'holding';
          this._schedule(hold, () => {
            this._phase = 'deleting';
            this._step();
          });
          return;
        }

        this._schedule(this._typeSpeedMs, () => this._step());
        return;
      }

      if (this._phase === 'deleting') {
        this._char -= 1;
        this.input.placeholder = this._word.slice(0, Math.max(0, this._char));

        if (this._char <= this._minChars) {
          this._phase = 'typing';
          this._schedule(this._gapMs, () => this._beginWord());
          return;
        }

        this._schedule(this._deleteSpeedMs, () => this._step());
      }
    }

    _onFocus() {
      // Stop immediately when user focuses
      this.stop();
      this.input.placeholder = this.defaultText;
    }

    _onInput() {
      // If user typed anything -> stop and keep default placeholder (won't show anyway)
      if ((this.input.value || '').trim().length > 0) {
        this.stop();
        return;
      }
      // If input cleared while focused, we still stay paused until blur/idle
    }

    _onBlur() {
      // Resume only after idle, and only if empty
      if ((this.input.value || '').trim().length > 0) return;
      if (prefersReducedMotion()) return;

      this.input.placeholder = this.defaultText;

      if (this._resumeTimeout) window.clearTimeout(this._resumeTimeout);
      this._resumeTimeout = window.setTimeout(() => {
        if (this._destroyed) return;
        if ((this.input.value || '').trim().length > 0) return;
        this.start();
      }, this.idleResumeMs);
    }

    _onVisibility() {
      if (document.hidden) {
        this.stop();
        return;
      }
      // only restart if empty + not focused
      if (!this.isPaused()) this.start();
    }

    destroy() {
      if (this._destroyed) return;
      this._destroyed = true;
      this.stop();
      this.input.removeEventListener('focus', this._onFocus);
      this.input.removeEventListener('blur', this._onBlur);
      this.input.removeEventListener('input', this._onInput);
      document.removeEventListener('visibilitychange', this._onVisibility);
      this.input.placeholder = this.defaultText;
    }
  }

  const instances = new WeakMap();

  const init = (root = document) => {
    if (prefersReducedMotion()) return;
    root.querySelectorAll(SELECTOR).forEach((input) => {
      if (instances.has(input)) return;
      instances.set(input, new RotatingPlaceholder(input));
    });
  };

  // Init on load and when Shopify sections re-render
  document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  window.addEventListener('pagehide', () => {
    // best-effort cleanup
    document.querySelectorAll(SELECTOR).forEach((input) => instances.get(input)?.destroy?.());
  });
})();


